/**
 * Prober service handles metrics capturing.
 *
 * @see app/docs/CapturingMetrics.md
 * */

import {throttle} from 'lodash'

import {getInstance, override} from '@base/di'
import {ActionEvent} from '@base/prober/events'
import {
  InteractionTemplate,
  withInteraction as proberWithInteraction,
} from '@base/prober/interaction'
import Prober from '@base/prober/Prober'

export function overrideProberInstance(instance?: any): () => void {
  return override(Prober, () => instance)
}

export function prober(): Prober | any {
  return getInstance(Prober)
}

export default prober

export function setProberDefaultEventData(data: any) {
  prober().setDefaultEventData(data)
}

export function getProberDefaultEventData(): any {
  return prober().getDefaultEventData()
}

export function setProberMetadata(data: any) {
  prober().setMetadata(data)
}

export function getProberMetadata(): any {
  return prober().getMetadata()
}

// Captures arbitrary user-facing action, like captureAction('Deals', 'Stage changed', {stageType: 'won'})
//
// Returns InteractionTemplate object which can be used as a template for chained child interaction.
// @see docs/CapturingMetrics.md
/* eslint-disable @typescript-eslint/no-use-before-define */
export function captureAction(
  category: string | ActionEvent,
  action?: string | null,
  meta?: any | null
): InteractionTemplate {
  const proberInstance = prober()
  if (proberInstance) {
    const event = proberInstance.captureAction(category, action, meta)
    return withInteraction(event.getChildInteractionData())
  }
  return withInteraction()
}
/* eslint-enable @typescript-eslint/no-use-before-define */

export function captureTiming(category: string, action: string, time: number) {
  const proberInstance = prober()
  if (proberInstance) proberInstance.captureTiming(category, action, time)
}
/* eslint-enable @typescript-eslint/no-use-before-define */

/**
 * Create a function to capture high velocity action.
 * Throttles capture calls using captureAction function that's being called not often than
 * `throttleTimeMs` milliseconds and reporting number of events during the last timespan in
 * the format { count: number }
 * @type {captureHighVelocityAction}
 * @param action
 * @return {captureHighVelocityAction}
 * @param throttleTimeMs
 */
export function createCaptureHighVelocityActionFunction(
  action: string,
  throttleTimeMs: number
) {
  let count = 0
  const throttleFn = throttle(() => {
    captureAction('counter', action, {count})
    count = 0
  }, throttleTimeMs)

  return function captureHighVelocityAction() {
    count += 1
    throttleFn()
  }
}

type ErrorMetaType = {
  // for debugging purposes only,
  debug?: boolean
  // how was the user notified?
  notified?: 'no' | 'errorPage' | 'feedbackNod' | 'feedbackStatus'
  // how big a portion of the application is affected by this error and may not work properly
  affects?:
    | 'none' // whole module will not be usable (module is already specified by controller)
    | 'app' // whole page will not be usable (page is already specified by controller & method)
    | 'module' // user initiated action will not finish properly
    | 'page' // main content won't show (list of deals on deals index, modal contents, etc.)
    | 'userAction' // side content won't show (list of tasks on dashboard, related product on deal details, etc.)
    | 'content' // UI element
    | 'sideContent' // Application level service
    | 'ui'
    | 'service' // voice is affected
    | 'locale'
    | 'voice'
    | 'notifications'
    | 'zendeskWidget'
  [key: string]: string | number | boolean | null | undefined
}

// Captures user-facing errors
export function captureError(
  category: string,
  exception: string | Error | any,
  meta?: ErrorMetaType | null
) {
  const proberInstance = prober()
  if (proberInstance) {
    proberInstance.captureException(category, exception, {
      fatal: true,
      notified: 'no',
      ...meta,
    })
  }
}

// Captures non-user-facing errors, which we should be notified about
export function captureWarning(
  category: string,
  warning: string | Error | any,
  meta?: ErrorMetaType | null
) {
  const proberInstance = prober()

  if (proberInstance) {
    proberInstance.captureException(category, warning, {
      fatal: false,
      notified: 'no',
      ...meta,
    })
  }
}

// Service wrapper ready to be passed around
const proberWrapper = {
  captureAction,
  captureError,
  captureWarning,
}

let withInteractionOverride = undefined

export const overrideWithInteraction = (newWithInteraction: any) => {
  withInteractionOverride = newWithInteraction
}

export const restoreWithInteraction = () => {
  withInteractionOverride = undefined
}

export function withInteraction(
  interaction?: any | undefined | null | InteractionTemplate
): InteractionTemplate {
  if (TESTING) {
    return withInteractionOverride
      ? // @ts-ignore
        withInteractionOverride(interaction)
      : proberWithInteraction(proberWrapper, interaction)
  }
  return proberWithInteraction(proberWrapper, interaction)
}

// Allows chaining interactions with current click event
export function withInputEventInteraction(event: {
  type: string
}): InteractionTemplate {
  if (event && event.type === 'click') {
    const proberInstance = prober()
    if (proberInstance && proberInstance.clickMonitor) {
      return withInteraction(
        proberInstance.clickMonitor.getInteractionForClickEvent(event)
      )
    }
  }
  return withInteraction()
}
